import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import {
  Gift, Plus, Loader2, Users, Trophy, Star,
  Clock, CheckCircle2, XCircle, Calendar, Phone, Mail,
  UserCheck, AlertCircle, TrendingUp, Award, Sparkles
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Status configuration with points eligibility
const statusConfig = {
  pending: { 
    label: 'Pending',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    icon: Clock,
    description: 'Waiting for contact',
    pointsEligible: false
  },
  contacted: { 
    label: 'Contacted',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: Phone,
    description: 'Initial contact made',
    pointsEligible: false
  },
  qualified: { 
    label: 'Qualified',
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: UserCheck,
    description: 'Name, number & interest verified',
    pointsEligible: true,
    points: 25
  },
  site_visit: { 
    label: 'Site Visit',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    icon: Calendar,
    description: 'Property visit completed',
    pointsEligible: true,
    points: 50
  },
  negotiation: { 
    label: 'Negotiation',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    icon: TrendingUp,
    description: 'In price discussion',
    pointsEligible: true,
    points: 25
  },
  booked: { 
    label: 'Booked',
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    icon: CheckCircle2,
    description: 'Property booked!',
    pointsEligible: true,
    points: 100
  },
  rewarded: { 
    label: 'Rewarded',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: Trophy,
    description: 'Reward credited',
    pointsEligible: false // Already credited
  },
  lost: { 
    label: 'Lost',
    color: 'bg-gray-100 text-gray-500 border-gray-200',
    icon: XCircle,
    description: 'Did not convert',
    pointsEligible: false
  },
  expired: { 
    label: 'Expired',
    color: 'bg-gray-100 text-gray-400 border-gray-200',
    icon: AlertCircle,
    description: 'Referral expired',
    pointsEligible: false
  }
};

export default function BuyerReferralsPage() {
  const [referrals, setReferrals] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [newReferral, setNewReferral] = useState({
    referred_name: '',
    referred_phone: '',
    referred_email: '',
    referred_occupation: '' // Added for qualification
  });

  const buyerData = JSON.parse(localStorage.getItem('buyerData') || '{}');
  const buyerId = buyerData?.id;

  useEffect(() => {
    if (buyerId) {
      loadReferrals();
      loadLoyalty();
    }
  }, [buyerId]);

  const loadReferrals = async () => {
    try {
      const token = localStorage.getItem('buyerToken');
      const res = await fetch(`${API}/api/buyer-portal/referrals?buyer_id=${buyerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setReferrals(data.referrals || []);
      }
    } catch (error) {
      toast.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  const loadLoyalty = async () => {
    try {
      const token = localStorage.getItem('buyerToken');
      const res = await fetch(`${API}/api/buyer-portal/loyalty?buyer_id=${buyerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setLoyalty(data);
      }
    } catch (error) {
      console.error('Failed to load loyalty');
    }
  };

  const handleCreateReferral = async () => {
    if (!newReferral.referred_name || !newReferral.referred_phone) {
      toast.error('Please fill in name and phone number');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('buyerToken');
      const res = await fetch(`${API}/api/buyer-portal/referrals?buyer_id=${buyerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newReferral)
      });

      if (res.ok) {
        toast.success('Referral submitted successfully!');
        setShowNewDialog(false);
        setNewReferral({ referred_name: '', referred_phone: '', referred_email: '', referred_occupation: '' });
        loadReferrals();
        loadLoyalty();
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create referral');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate referral statistics
  const getRefferalStats = () => {
    const total = referrals.length;
    const byStatus = {};
    let totalPointsEarned = 0;
    let potentialPoints = 0;

    referrals.forEach(ref => {
      const status = ref.status || 'pending';
      byStatus[status] = (byStatus[status] || 0) + 1;
      
      // Calculate points
      if (ref.reward_points && ref.reward_status === 'credited') {
        totalPointsEarned += ref.reward_points;
      }
      
      // Calculate potential points for qualified leads
      const config = statusConfig[status];
      if (config?.pointsEligible && ref.reward_status !== 'credited') {
        potentialPoints += config.points || 0;
      }
    });

    const qualified = (byStatus.qualified || 0) + (byStatus.site_visit || 0) + 
                     (byStatus.negotiation || 0) + (byStatus.booked || 0) + (byStatus.rewarded || 0);
    const pending = byStatus.pending || 0;
    const lost = (byStatus.lost || 0) + (byStatus.expired || 0);

    return { total, byStatus, qualified, pending, lost, totalPointsEarned, potentialPoints };
  };

  const stats = getRefferalStats();

  const getTierProgress = () => {
    if (!loyalty) return { progress: 0, next: 'Silver', remaining: 3 };
    
    const tier = loyalty.loyalty_tier || 'bronze';
    const successful = loyalty.successful_referrals || 0;
    
    const tiers = {
      bronze: { next: 'Silver', target: 3 },
      silver: { next: 'Gold', target: 7 },
      gold: { next: 'Platinum', target: 15 },
      platinum: { next: null, target: 15 }
    };
    
    const tierData = tiers[tier] || tiers.bronze;
    const progress = Math.min((successful / tierData.target) * 100, 100);
    const remaining = Math.max(tierData.target - successful, 0);
    
    return { progress, next: tierData.next, remaining };
  };

  const tierProgress = getTierProgress();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Referral Program</h1>
          <p className="text-muted-foreground">Refer friends and earn rewards</p>
        </div>
        <Button onClick={() => setShowNewDialog(true)} className="bg-amber-600 hover:bg-amber-700">
          <Plus className="h-4 w-4 mr-2" />
          Refer a Friend
        </Button>
      </div>

      {/* Loyalty Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Your Tier</p>
                <p className="text-3xl font-bold capitalize">{loyalty?.loyalty_tier || 'Bronze'}</p>
              </div>
              <Trophy className="h-12 w-12 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Total Referrals</p>
                <p className="text-3xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.qualified} qualified, {stats.pending} pending
                </p>
              </div>
              <Users className="h-12 w-12 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Points Earned</p>
                <p className="text-3xl font-bold text-green-600">{loyalty?.loyalty_points || 0}</p>
                {stats.potentialPoints > 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    +{stats.potentialPoints} potential
                  </p>
                )}
              </div>
              <Star className="h-12 w-12 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Qualified Leads</p>
                <p className="text-3xl font-bold text-green-600">{stats.qualified}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Earning points
                </p>
              </div>
              <UserCheck className="h-12 w-12 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tier Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-600" />
            Progress to Next Tier
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="font-medium capitalize">{loyalty?.loyalty_tier || 'Bronze'}</span>
              {tierProgress.next ? (
                <span className="text-sm text-muted-foreground">
                  {tierProgress.remaining} more referral{tierProgress.remaining !== 1 ? 's' : ''} to {tierProgress.next}
                </span>
              ) : (
                <span className="text-sm text-green-600 font-medium">Maximum tier reached!</span>
              )}
            </div>
            <Progress value={tierProgress.progress} className="h-3" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Bronze</span>
              <span>Silver (3)</span>
              <span>Gold (7)</span>
              <span>Platinum (15)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rewards You Can Earn */}
      <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-amber-600" />
            Rewards You Can Earn
          </CardTitle>
          <CardDescription>Earn exciting rewards for every successful referral</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-white rounded-lg border border-amber-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-green-100 rounded-full">
                  <Star className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold">Per Successful Referral</h4>
                  <p className="text-sm text-muted-foreground">When your friend books a property</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Loyalty Points</span>
                  <span className="font-semibold text-green-600">+100 points</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Cash Reward</span>
                  <span className="font-semibold text-green-600">Up to ₹25,000</span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-white rounded-lg border border-amber-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-100 rounded-full">
                  <Trophy className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold">Tier Benefits</h4>
                  <p className="text-sm text-muted-foreground">Level up for exclusive perks</p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Bronze</span>
                  <span>Base rewards</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400 font-medium">Silver (3+)</span>
                  <span className="text-blue-600">+10% bonus</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-yellow-600 font-medium">Gold (7+)</span>
                  <span className="text-blue-600">+20% bonus</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-purple-600 font-medium">Platinum (15+)</span>
                  <span className="text-blue-600">+30% bonus + VIP</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-white rounded-lg border border-amber-200">
            <h4 className="font-semibold mb-2">Redemption Options</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="text-center p-2 bg-amber-50 rounded">
                <p className="font-medium">Maintenance Credit</p>
                <p className="text-xs text-muted-foreground">Use for society fees</p>
              </div>
              <div className="text-center p-2 bg-amber-50 rounded">
                <p className="font-medium">Gift Vouchers</p>
                <p className="text-xs text-muted-foreground">Amazon, Flipkart</p>
              </div>
              <div className="text-center p-2 bg-amber-50 rounded">
                <p className="font-medium">Bank Transfer</p>
                <p className="text-xs text-muted-foreground">Direct to account</p>
              </div>
              <div className="text-center p-2 bg-amber-50 rounded">
                <p className="font-medium">Home Services</p>
                <p className="text-xs text-muted-foreground">Interior, cleaning</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referrals List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Your Referrals ({referrals.length})</CardTitle>
              <CardDescription>Track the status of each referral and points earned</CardDescription>
            </div>
            {/* Status Legend */}
            <div className="hidden md:flex items-center gap-2 text-xs">
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-yellow-400" />
                Pending
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-400" />
                Qualified
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                Booked
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <div className="text-center py-8">
              <Gift className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-medium text-lg mb-2">No referrals yet</h3>
              <p className="text-muted-foreground mb-4">
                Start referring friends to earn rewards and level up!
              </p>
              <Button onClick={() => setShowNewDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Make Your First Referral
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {referrals.map((referral) => {
                const status = referral.status || 'pending';
                const config = statusConfig[status] || statusConfig.pending;
                const StatusIcon = config.icon;
                const isQualified = ['qualified', 'site_visit', 'negotiation', 'booked', 'rewarded'].includes(status);
                const pointsAwarded = referral.reward_points && referral.reward_status === 'credited';
                
                return (
                  <div 
                    key={referral.id} 
                    className={`p-4 border rounded-lg transition-all hover:shadow-md cursor-pointer ${
                      isQualified ? 'border-green-200 bg-green-50/30' : ''
                    }`}
                    onClick={() => setSelectedReferral(referral)}
                    data-testid={`referral-${referral.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-full border ${config.color}`}>
                          <StatusIcon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{referral.referred_name}</p>
                            {isQualified && (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                                <UserCheck className="h-3 w-3 mr-1" />
                                Qualified
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {referral.referred_phone}
                            </span>
                            {referral.referred_email && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {referral.referred_email}
                              </span>
                            )}
                          </div>
                          {referral.referred_occupation && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Occupation: {referral.referred_occupation}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right flex-shrink-0">
                        <Badge className={config.color}>
                          {config.label}
                        </Badge>
                        
                        {/* Points Display */}
                        {pointsAwarded ? (
                          <div className="mt-2">
                            <p className="text-sm font-semibold text-green-600 flex items-center justify-end gap-1">
                              <Award className="h-4 w-4" />
                              +{referral.reward_points} pts
                            </p>
                            <p className="text-xs text-green-500">Credited</p>
                          </div>
                        ) : isQualified && config.points ? (
                          <div className="mt-2">
                            <p className="text-sm font-medium text-amber-600 flex items-center justify-end gap-1">
                              <Sparkles className="h-4 w-4" />
                              +{config.points} pts
                            </p>
                            <p className="text-xs text-amber-500">Potential</p>
                          </div>
                        ) : null}
                        
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(referral.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    {/* Progress indicator for qualified leads */}
                    {isQualified && !pointsAwarded && (
                      <div className="mt-3 pt-3 border-t border-green-100">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-green-700">{config.description}</span>
                          <span className="text-green-600 font-medium">
                            Points will be credited on booking
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* How it Works */}
      <Card>
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="text-center p-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="font-bold text-amber-600">1</span>
              </div>
              <h4 className="font-medium mb-1">Refer</h4>
              <p className="text-sm text-muted-foreground">Share your friend's contact details</p>
            </div>
            <div className="text-center p-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="font-bold text-amber-600">2</span>
              </div>
              <h4 className="font-medium mb-1">Site Visit</h4>
              <p className="text-sm text-muted-foreground">Your friend visits the property</p>
            </div>
            <div className="text-center p-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="font-bold text-amber-600">3</span>
              </div>
              <h4 className="font-medium mb-1">Booking</h4>
              <p className="text-sm text-muted-foreground">They book a property</p>
            </div>
            <div className="text-center p-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="font-bold text-green-600">4</span>
              </div>
              <h4 className="font-medium mb-1">Earn</h4>
              <p className="text-sm text-muted-foreground">Get points & rewards!</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* New Referral Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-amber-600" />
              Refer a Friend
            </DialogTitle>
            <DialogDescription>
              Fill in your friend's details. Points are awarded when they're qualified!
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Friend's Name *</Label>
              <Input
                placeholder="Enter your friend's name"
                value={newReferral.referred_name}
                onChange={(e) => setNewReferral({ ...newReferral, referred_name: e.target.value })}
                data-testid="input-referral-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Phone Number *</Label>
              <Input
                placeholder="+91 9876543210"
                value={newReferral.referred_phone}
                onChange={(e) => setNewReferral({ ...newReferral, referred_phone: e.target.value })}
                data-testid="input-referral-phone"
              />
            </div>

            <div className="space-y-2">
              <Label>Occupation / What they do</Label>
              <Input
                placeholder="e.g., IT Professional, Business Owner"
                value={newReferral.referred_occupation}
                onChange={(e) => setNewReferral({ ...newReferral, referred_occupation: e.target.value })}
                data-testid="input-referral-occupation"
              />
              <p className="text-xs text-muted-foreground">
                This helps qualify the lead faster
              </p>
            </div>

            <div className="space-y-2">
              <Label>Email (Optional)</Label>
              <Input
                type="email"
                placeholder="friend@email.com"
                value={newReferral.referred_email}
                onChange={(e) => setNewReferral({ ...newReferral, referred_email: e.target.value })}
              />
            </div>

            {/* Points Info Card */}
            <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200">
              <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Points You Can Earn
              </h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-amber-700">When Qualified</span>
                  <span className="font-semibold text-green-600">+25 pts</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-amber-700">On Site Visit</span>
                  <span className="font-semibold text-green-600">+50 pts</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-amber-700">On Booking</span>
                  <span className="font-semibold text-green-600">+100 pts</span>
                </div>
              </div>
              <p className="text-xs text-amber-600 mt-2">
                * Qualified = Name, number & interest verified by our team
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateReferral} 
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit Referral
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Referral Detail Dialog */}
      <Dialog open={!!selectedReferral} onOpenChange={() => setSelectedReferral(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Referral Details</DialogTitle>
          </DialogHeader>
          
          {selectedReferral && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <Badge className={statusConfig[selectedReferral.status]?.color || 'bg-gray-100'}>
                  {statusConfig[selectedReferral.status]?.label || selectedReferral.status}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {new Date(selectedReferral.created_at).toLocaleDateString()}
                </span>
              </div>

              {/* Contact Info */}
              <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">Name</p>
                  <p className="font-medium">{selectedReferral.referred_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedReferral.referred_phone}</p>
                </div>
                {selectedReferral.referred_email && (
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedReferral.referred_email}</p>
                  </div>
                )}
                {selectedReferral.referred_occupation && (
                  <div>
                    <p className="text-xs text-muted-foreground">Occupation</p>
                    <p className="font-medium">{selectedReferral.referred_occupation}</p>
                  </div>
                )}
              </div>

              {/* Qualification Status */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-green-600" />
                  Qualification Checklist
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span>Name provided</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span>Phone number provided</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedReferral.referred_occupation ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-yellow-500" />
                    )}
                    <span>Occupation/Interest info</span>
                  </div>
                </div>
              </div>

              {/* Points Info */}
              {selectedReferral.reward_points > 0 && selectedReferral.reward_status === 'credited' ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-green-700 font-medium">Points Earned</span>
                    <span className="text-xl font-bold text-green-600">
                      +{selectedReferral.reward_points}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <p className="text-amber-800">
                    Points will be credited when this referral is qualified and progresses through the sales pipeline.
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedReferral(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
